import type { ActivityEvent, NotificationItem } from '../socialApi';

function payloadString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function encoded(value: string): string {
  return encodeURIComponent(value);
}

function venuePath(venueId: string, impressionId?: string | null): string {
  const base = `/venue/${encoded(venueId)}`;
  return impressionId ? `${base}?impression=${encoded(impressionId)}` : base;
}

export function isModerationNotification(item: NotificationItem): boolean {
  return [
    'moderation_violation_confirmed',
    'moderation_violation_overturned',
    'publication_hold_released',
  ].includes(item.type as string);
}

export function notificationSenderName(item: NotificationItem): string {
  return isModerationNotification(item)
    ? 'Хоппер'
    : item.actor?.display_name?.trim()
      || payloadString(item.payload.follower_display_name)
      || 'Пользователь Хоппера';
}

export function notificationText(item: NotificationItem): string {
  const actor = item.actor?.display_name?.trim() || 'Пользователь Хоппера';

  switch (item.type as string) {
    case 'new_follower':
      return `${actor} теперь следует за вами`;
    case 'followed_impression':
      return `${actor} поделился впечатлением`;
    case 'followed_discovery':
      return `${actor} открыл новое место`;
    case 'level_up': {
      const rawLevel = item.payload.level;
      const level = typeof rawLevel === 'number' || typeof rawLevel === 'string'
        ? String(rawLevel).trim()
        : '';
      return level ? `Вы достигли уровня ${level}!` : 'Вы достигли нового уровня!';
    }
    case 'moderation_violation_confirmed':
      return 'Хоппер: подтверждено нарушение правил';
    case 'moderation_violation_overturned':
      return 'Хоппер: решение модерации отменено, XP восстановлен';
    case 'publication_hold_released':
      return 'Хоппер: ограничение на публикации снято';
    default:
      return 'Новое уведомление Хоппера';
  }
}

export function notificationDestination(item: NotificationItem): string | null {
  switch (item.type as string) {
    case 'new_follower':
      return item.actor?.id ? `/profile/${encoded(item.actor.id)}` : null;
    case 'followed_impression': {
      const venueId = payloadString(item.payload.venue_id);
      if (!venueId) return null;
      return venuePath(venueId, payloadString(item.payload.impression_id));
    }
    case 'followed_discovery': {
      const venueId = payloadString(item.payload.venue_id);
      return venueId ? venuePath(venueId) : null;
    }
    case 'level_up':
      return '/profile/achievements';
    case 'moderation_violation_confirmed':
    case 'moderation_violation_overturned': {
      const violationId = payloadString(item.payload.violation_id);
      return violationId
        ? `/profile?section=xp&moderation=${encoded(violationId)}`
        : '/profile?section=xp';
    }
    case 'publication_hold_released': {
      const restriction = item.payload.restriction;
      const decisionId = restriction && typeof restriction === 'object'
        ? payloadString((restriction as Record<string, unknown>).latest_decision_id)
        : null;
      return decisionId
        ? `/profile?section=xp&moderation=${encoded(decisionId)}`
        : '/profile?section=xp';
    }
    default:
      return null;
  }
}

export function activitySubjectDestination(
  event: ActivityEvent,
  currentUserId: string | null,
): string | null {
  const venueId = event.venue?.id ?? payloadString(event.payload.venue_id);
  const isOwnUserEvent = Boolean(currentUserId && event.author.type !== 'venue' && event.author.id === currentUserId);
  const type = event.type as string;

  switch (type) {
    case 'impression_created':
      return venueId ? venuePath(venueId, payloadString(event.payload.impression_id)) : null;
    case 'follow_created': {
      const followedUserId = payloadString(event.payload.followed_user_id);
      return followedUserId ? `/profile/${encoded(followedUserId)}` : null;
    }
    case 'new_place_discovered':
      return venueId ? venuePath(venueId) : null;
    case 'photo_uploaded':
      if (isOwnUserEvent) return '/profile/photos';
      return event.author.id ? `/profile/${encoded(event.author.id)}` : null;
    case 'venue_photo_published':
      return venueId ? `/venue/${encoded(venueId)}/media` : null;
    case 'venue_offer_published': {
      const offerId = payloadString(event.payload.offer_id);
      return offerId ? `/offers/${encoded(offerId)}` : null;
    }
    case 'venue_event_published': {
      const eventId = payloadString(event.payload.event_id);
      return eventId ? `/events/${encoded(eventId)}` : null;
    }
    case 'venue_post_published': {
      const postId = payloadString(event.payload.post_id);
      return postId ? `/posts/${encoded(postId)}` : null;
    }
    case 'achievement_unlocked':
    case 'achievement_highlight':
      if (isOwnUserEvent) return '/profile/achievements';
      return event.author.id ? `/profile/${encoded(event.author.id)}` : null;
    case 'city_leader_changed':
    case 'city_weekly_champion':
    case 'venue_leader_changed':
    case 'discovery_milestone':
      return event.author.id ? `/profile/${encoded(event.author.id)}` : null;
    case 'route_published':
    case 'route_completed':
      return null;
    default:
      return null;
  }
}
